import type {
  LocationQuery,
  LocationQueryRaw,
  LocationQueryValue,
  RouteLocationNormalizedLoaded,
  RouteLocationRaw,
} from 'vue-router'
import {
  createOpenProjectSessionInput,
  type OpenProjectSessionInput,
} from '@/services/api/open-project-input'

export const OPEN_PROJECT_REPOSITORY_ID_QUERY_KEY = 'repositoryId'
export const OPEN_PROJECT_BRANCH_ID_QUERY_KEY = 'branchId'
export const OPEN_PROJECT_COMMIT_ID_QUERY_KEY = 'commitId'

export interface OpenProjectRouteContext {
  /** 当前路由中的项目 ID 原文。 */
  readonly projectId: string
  /** 可直接传入 OpenProject 的后端请求上下文。 */
  readonly sessionInput: OpenProjectSessionInput
}

export type OpenProjectRouteName = 'editor' | 'preview' | 'version-preview'

/**
 * 规范化 Vue Router params 中的单值参数。
 *
 * @param value 路由参数原始值。
 * @param fieldName 错误信息中使用的字段名。
 * @returns 单个字符串参数。
 */
function normalizeRouteParam(value: string | string[] | undefined, fieldName: string): string {
  const normalizedValue = Array.isArray(value) ? value[0] : value
  if (typeof normalizedValue !== 'string' || !normalizedValue.trim()) {
    throw new Error(`OpenProject 路由参数无效：${fieldName} 不能为空`)
  }
  return normalizedValue
}

/**
 * 规范化 Vue Router query 中的单值字符串。
 *
 * @param value query 原始值。
 * @returns 单个字符串；缺省或数组首项非字符串时返回 null。
 */
function normalizeQueryValue(value: LocationQueryValue | LocationQueryValue[]): string | null {
  if (Array.isArray(value)) {
    return typeof value[0] === 'string' ? value[0] : null
  }
  return typeof value === 'string' ? value : null
}

/**
 * 从当前路由解析 OpenProject 打开上下文。
 *
 * @param route 当前 Vue Router 路由对象。
 * @returns 页面和 API service 共享的打开项目上下文。
 */
export function resolveOpenProjectRouteContext(
  route: RouteLocationNormalizedLoaded,
): OpenProjectRouteContext {
  const projectId = normalizeRouteParam(route.params.projectId, 'projectId')
  const repositoryId = normalizeQueryValue(route.query[OPEN_PROJECT_REPOSITORY_ID_QUERY_KEY])
  const branchId = normalizeQueryValue(route.query[OPEN_PROJECT_BRANCH_ID_QUERY_KEY])
  const commitId = normalizeQueryValue(route.query[OPEN_PROJECT_COMMIT_ID_QUERY_KEY])

  return {
    projectId,
    sessionInput: createOpenProjectSessionInput({
      projectId,
      repositoryId,
      branchId,
      commitId,
    }),
  }
}

/**
 * 从 query 中只保留 OpenProject 打开上下文。
 *
 * @param query 当前路由 query。
 * @returns 可用于路由跳转的 OpenProject query。
 */
export function pickOpenProjectRouteQuery(query: LocationQuery): LocationQueryRaw {
  return buildOpenProjectRouteQuery({
    repositoryId: normalizeQueryValue(query[OPEN_PROJECT_REPOSITORY_ID_QUERY_KEY]),
    branchId: normalizeQueryValue(query[OPEN_PROJECT_BRANCH_ID_QUERY_KEY]),
    commitId: normalizeQueryValue(query[OPEN_PROJECT_COMMIT_ID_QUERY_KEY]),
  })
}

/**
 * 构造 OpenProject query。
 *
 * @param input 仓库和分支 ID 原文。
 * @returns 仅包含非空 OpenProject query 的对象。
 */
export function buildOpenProjectRouteQuery(input: {
  readonly repositoryId?: string | null
  readonly branchId?: string | null
  readonly commitId?: string | null
}): LocationQueryRaw {
  const repositoryId = input.repositoryId?.trim() ?? ''
  const branchId = input.branchId?.trim() ?? ''
  const commitId = input.commitId?.trim() ?? ''

  return {
    ...(repositoryId ? { [OPEN_PROJECT_REPOSITORY_ID_QUERY_KEY]: repositoryId } : {}),
    ...(branchId ? { [OPEN_PROJECT_BRANCH_ID_QUERY_KEY]: branchId } : {}),
    ...(commitId ? { [OPEN_PROJECT_COMMIT_ID_QUERY_KEY]: commitId } : {}),
  }
}

/**
 * 生成保留 OpenProject 上下文的命名路由。
 *
 * @param name 目标页面路由名。
 * @param context 当前 OpenProject 路由上下文。
 * @param extraQuery 目标页面额外 query。
 * @returns 可直接传给 `router.push` / `router.replace` 的路由对象。
 */
export function buildOpenProjectRouteLocation(
  name: OpenProjectRouteName,
  context: OpenProjectRouteContext,
  extraQuery: LocationQueryRaw = {},
): RouteLocationRaw {
  return {
    name,
    params: {
      projectId: context.projectId,
    },
    query: {
      ...buildOpenProjectRouteQuery({
        repositoryId: context.sessionInput.repositoryIdText,
        branchId: context.sessionInput.branchIdText,
        commitId: context.sessionInput.commitIdText,
      }),
      ...extraQuery,
    },
  }
}
